import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import type WorkflowRunSummaryView from '@UI/Store/WorkflowRuns/Views/WorkflowRunSummaryView';
import { css } from '@emotion/react';
import { ClipboardCheck } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@verclam/ui-kit';

const rowStyle = css`
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: center;
	padding: 1rem 1.25rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		row-gap: 0.5rem;
	}
`;

const tileStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.375rem;
	height: 2.375rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.states.success.surface};
	color: ${theme.states.success.ink};
`;

const textBlockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.1875rem;
	min-width: 0;
`;

const titleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const subtitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunSummaryView;
};

/**
 * One preparation whose documents are all generated and verified: read-only access only. Exactly four
 * cells on the list's grid: tile, title and completion date, verified document count, link.
 */
const CompletedPreparationRow: React.FC<Props> = ({ subject, preparation }) => {
	const navigate = useNavigate();

	const handleConsultClick = React.useCallback(() => {
		navigate(`${buildSubjectPath(subject)}/preparations/${preparation.id}`);
	}, [navigate, subject, preparation.id]);

	const documentCount = React.useMemo(
		() => preparation.documents.filter((document) => document.state.value === 'verified').length,
		[preparation.documents],
	);

	return (
		<div css={rowStyle}>
			<div css={tileStyle} aria-hidden="true">
				<ClipboardCheck size={18} />
			</div>
			<div css={textBlockStyle}>
				<h3 css={titleStyle}>{preparation.label}</h3>
				<p css={subtitleStyle}>
					{preparation.completedAt !== null
						? `Terminée le ${formatLongDate(preparation.completedAt.toISOString())}`
						: 'Terminée'}
				</p>
			</div>
			<div css={subtitleStyle}>
				{documentCount <= 1
					? `${documentCount} document vérifié`
					: `${documentCount} documents vérifiés`}
			</div>
			<div>
				<Button
					type="button"
					severity="secondary"
					outlined
					size="sm"
					label="Consulter"
					onClick={handleConsultClick}
				/>
			</div>
		</div>
	);
};

export default CompletedPreparationRow;
